import { useState } from "react";

const CandidateForm = ({
  loading,
  closeModal,
  onSubmit,
  campaignOptions,
  initialValues,
  errors = {}, // Added errors prop to display validation messages
  notification, setNotification
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [resumeFile, setResumeFile] = useState(null); // State to manage the uploaded file
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    // console.log(e.target)
    const fileInput = e.target; // Get the input element
    const file = fileInput.files[0];
  
    if (file && file.size > 2 * 1024 * 1024) { // 2MB limit
      setNotification("File should not exceed 2MB");
      fileInput.value = ""; // Reset file input
      setResumeFile(null); // Clear the state
      return;
    }
    setResumeFile(file); 

  };

  const validate = () => {
    const validationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const onlyTextRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.name.trim()) {
      validationErrors.name = "Name is required.";
    } else if (!onlyTextRegex.test(formData.name.trim())) {
      validationErrors.name = "Name should contain only letters and spaces.";
    }

    if (!formData.email.trim()) {
      validationErrors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email.trim())) {
      validationErrors.email = "Invalid email format.";
    }

    if (!formData.contact_no.trim()) {
      validationErrors.contact_no = "Contact number is required.";
    } else if (!phoneRegex.test(formData.contact_no.trim())) {
      validationErrors.contact_no = "Contact number must be exactly 10 digits.";
    }

    if (!formData.experience || formData.experience < 0) {
      validationErrors.experience = "Experience is required and must be 0 or greater.";
    }

    // Date of birth validation - must be at least 18 years old
    if (formData.date_of_birth) {
      const birthDate = new Date(formData.date_of_birth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        validationErrors.date_of_birth = "Person must be at least 18 years old.";
      }
    }

    if (!formData.resume_path && !resumeFile) {
      validationErrors.resume_path = "Resume is required.";
    }    

    return validationErrors;
  };
  
  
  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }	

    const submissionData = new FormData(); // Use FormData for file upload
    submissionData.append("name", formData.name);
    submissionData.append("email", formData.email);
    submissionData.append("contact_no", formData.contact_no); // Append contact number
    submissionData.append("campaign_id", formData.campaign_id);
    submissionData.append("experience", formData.experience);
    if (formData.date_of_birth) {
      submissionData.append("date_of_birth", formData.date_of_birth);
    }

    // Append file only if it's selected
    if (resumeFile) {
      submissionData.append("resume_path", resumeFile);
    } else if (!formData.resume_path) {
      setFormErrors({ resume_path: "Resume is required." });
      return;
    }
    onSubmit(submissionData); 
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm dark:text-white font-medium text-gray-700">
            Name <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.name ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm dark:text-white font-medium text-gray-700">
            Email <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.email ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
        </div>

        {/* Contact Number Field */}
        <div>
          <label htmlFor="contact_no" className="block text-sm dark:text-white font-medium text-gray-700">
            Contact Number <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="tel"
            id="contact_no"
            name="contact_no"
            value={formData.contact_no}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
              setFormData({ ...formData, contact_no: onlyNums });
            }}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.contact_no ? "border-red-500" : "border-gray-300"
            }`}
            maxLength={10} // Optional: Limit to 10 digits
            required
          />
          {formErrors.contact_no && (
            <p className="text-red-500 text-sm mt-1">{formErrors.contact_no}</p>
          )}
        </div>

        {/* Date of Birth Field */}
        <div>
          <label htmlFor="date_of_birth" className="block text-sm dark:text-white font-medium text-gray-700">
            Date of Birth
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth || ""}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.date_of_birth ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.date_of_birth && (
            <p className="text-red-500 text-sm mt-1">{formErrors.date_of_birth}</p>
          )}
        </div>

        {/* Experience Field */}
        <div>
          <label htmlFor="experience" className="block text-sm dark:text-white font-medium text-gray-700">
            Experience (Years) <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="number"
            id="experience"
            name="experience"
            value={formData.experience || ""}
            onChange={handleChange}
            min="0"
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.experience ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.experience && (
            <p className="text-red-500 text-sm mt-1">{formErrors.experience}</p>
          )}
        </div>

        {/* Campaign Selection */}
        <div>
          <label htmlFor="campaign_id" className="block text-sm dark:text-white font-medium text-gray-700">
            Campaign <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            id="campaign_id"
            name="campaign_id"
            value={formData.campaign_id}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.campaign_id ? "border-red-500" : "border-gray-300"
            }`}
            required
          >
            <option value="">Select a campaign</option>
            {campaignOptions.map((campaign) => (
              <option key={campaign.value} value={campaign.value}>
                {campaign.label}
              </option>
            ))}
          </select>
          {formErrors.campaign_id && (
            <p className="text-red-500 text-sm mt-1">{formErrors.campaign_id}</p>
          )}
        </div>



        {/* Resume Upload */}
        <div>
          <label htmlFor="resume_path" className="block text-sm dark:text-white font-medium text-gray-700">
            Resume {(!formData.resume_path && <span className="text-red-500 ml-1">*</span>)}
          </label>

          {/* Show existing resume link if available */}
          {formData.resume_path && (
            <div className="mb-2">
              <a
                href={`${"https://hrmspayroll.prepstripe.com/storage/"}${formData.resume_path}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 underline"
              >
                View Existing Resume
              </a>
            </div>
          )}

          <input
            type="file"
            id="resume_path"
            name="resume_path"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className={`mt-1 block w-full border rounded-md p-2 ${
              formErrors.resume_path ? "border-red-500" : "border-gray-300"
            }`}
          />
          
          {formErrors.resume_path && (
            <p className="text-red-500 text-sm mt-1">{formErrors.resume_path}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 items-center">
          <button
            type="button"
            onClick={closeModal}
            className="px-12 py-2 text-black font-medium hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center gap-1 px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {initialValues.id ? "Update Candidate" : "Add Candidate"}
            {loading && (
            <svg
              className="animate-spin h-5 w-5 ml-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CandidateForm;